import { ReactNode, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Clock, LogOut, Crown, ShieldX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import MfaEnroll from '@/components/MfaEnroll';
import MfaVerify from '@/components/MfaVerify';
import NoTenantAssigned from '@/components/NoTenantAssigned';
import posLogo from '@/assets/posvision-logo.png';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useDeviceHeartbeat } from '@/hooks/useDeviceHeartbeat';
import { useRemoteCommandPoller } from '@/hooks/useRemoteCommandPoller';

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const {
    user,
    loading,
    approved,
    tenantId,
    signOut,
    mfaRequired,
    mfaEnrolled,
    mfaVerified,
    mustChangePassword,
    refreshMfa,
    subscriptionStatus,
    userRole,
  } = useAuth();
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  // Heartbeat + poll des commandes Assistance: brancher ici (et non dans
  // AppLayout) pour que ça tourne aussi en mode plein écran (PFull: caisse,
  // cuisine, kiosk).
  useDeviceHeartbeat();
  useRemoteCommandPoller();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Force password change (admin-created accounts with temporary password)
  if (mustChangePassword && userRole !== 'super_admin') {
    return <Navigate to="/changer-mot-de-passe" replace />;
  }

  // Users without a tenant: self-service onboarding retiré le 2026-05-12 (refonte à venir).
  // Affichage d'un écran "compte non provisionné" en attendant. Provisionnement
  // manuel par super_admin via Portail Admin > CreateClientWizard.
  if (!tenantId && userRole !== 'super_admin') {
    return <NoTenantAssigned />;
  }

  // Show pending approval screen (for users added to an existing tenant by admin)
  if (approved === false) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(224,50%,14%)] to-[hsl(224,55%,8%)] px-4">
        <Card className="w-full max-w-md border-0 bg-[hsl(224,50%,12%)] text-white shadow-2xl">
          <CardContent className="space-y-6 p-8 text-center">
            <img src={posLogo} alt="VisionPos 360" className="mx-auto h-12 object-contain" />
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-white">Compte en attente de validation</h2>
              <p className="text-sm text-gray-400">
                Votre inscription a bien été prise en compte. Un administrateur doit valider votre
                accès avant que vous puissiez utiliser l'application.
              </p>
            </div>
            <Button
              variant="outline"
              className="gap-2 border-white/20 text-white hover:bg-white/10"
              onClick={signOut}
            >
              <LogOut className="h-4 w-4" />
              Se déconnecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show suspended account screen (trial expired) — super_admin bypasses
  if (subscriptionStatus === 'suspended' && userRole !== 'super_admin') {
    const handleUpgrade = async () => {
      setUpgradeLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('stripe-billing', {
          body: {
            action: 'create_checkout',
            tenant_id: (await supabase.rpc('get_my_tenant_id')).data,
            plan_key: 'essentiel',
            customer_email: user.email,
          },
        });
        if (error) throw error;
        if (data?.url) {
          window.open(data.url, '_blank');
        } else if (data?.error) {
          throw new Error(data.error);
        }
      } catch {
        toast.error('Erreur lors de la redirection vers le paiement');
      } finally {
        setUpgradeLoading(false);
      }
    };

    return (
      <div className="dark flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(224,50%,14%)] to-[hsl(224,55%,8%)] px-4">
        <Card className="w-full max-w-md border-0 bg-[hsl(224,50%,12%)] text-white shadow-2xl">
          <CardContent className="space-y-6 p-8 text-center">
            <img src={posLogo} alt="VisionPos 360" className="mx-auto h-12 object-contain" />
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <ShieldX className="h-8 w-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-white">Période d'essai terminée</h2>
              <p className="text-sm text-gray-400">
                Votre essai gratuit de 7 jours est arrivé à son terme. Pour continuer à utiliser
                VisionPos 360, veuillez souscrire à un abonnement.
              </p>
            </div>
            <div className="space-y-3">
              <Button
                className="navy-gradient w-full gap-2"
                onClick={handleUpgrade}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Crown className="h-4 w-4" />
                )}
                Souscrire un abonnement
              </Button>
              <Button
                variant="outline"
                className="w-full gap-2 border-white/20 text-white hover:bg-white/10"
                onClick={signOut}
              >
                <LogOut className="h-4 w-4" />
                Se déconnecter
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Contactez votre administrateur si vous pensez qu'il s'agit d'une erreur.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // MFA: admin/super_admin must enroll if not enrolled
  if (mfaRequired && !mfaEnrolled) {
    return <MfaEnroll mandatory onComplete={() => refreshMfa()} />;
  }

  // MFA: enrolled but not verified this session
  if (mfaEnrolled && !mfaVerified) {
    return <MfaVerify onVerified={() => refreshMfa()} onSignOut={signOut} />;
  }

  return <>{children}</>;
}
